import React from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage } from '../components/ui'
import TradeEvaluationView from '../components/TradeEvaluationView'

// A trade someone shared by link, readable without an account.
//
// The redaction is entirely server-side — Trades::SharedView decides what this
// page is allowed to know, and there is nothing here to "hide". That matters:
// anything filtered in the browser would still be sitting in the response body.
// So this renders whatever it is given and simply omits the owner's controls.
export default function SharedTrade() {
  const { token } = useParams()

  const shared = useQuery({
    queryKey: ['sharedTrade', token],
    queryFn: () => api.sharedTrade(token),
    retry: false,
  })

  if (shared.isPending) {
    return (
      <Layout wide publicView>
        <Spinner label="Loading trade…" />
      </Layout>
    )
  }

  if (shared.error) {
    return (
      <Layout wide publicView>
        <Card title="This link isn’t available">
          <p className="text-sm text-slate-300">
            It may have been turned off by whoever shared it, or the trade may
            have been deleted.
          </p>
        </Card>
      </Layout>
    )
  }

  const trade = shared.data?.trade
  const results = shared.data?.evaluation?.results
  const weeks = results?.weeks || []

  if (!results) {
    return (
      <Layout wide publicView>
        <Card title="Nothing to show yet">
          <p className="text-sm text-slate-300">
            This trade hasn’t been evaluated.
          </p>
        </Card>
      </Layout>
    )
  }

  return (
    <Layout wide publicView>
      <div className="mb-4">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-lg font-semibold text-slate-100">
            {trade?.name || 'Shared trade'}
          </h1>
          {trade?.exploratory && (
            <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-slate-600 text-slate-400">
              Exploration
            </span>
          )}
        </div>
        <p className="text-sm text-slate-400">
          Shared trade
          {weeks.length > 0 &&
            ` · weeks ${weeks[0].week}–${weeks[weeks.length - 1].week}`}
          {' · team and league names are hidden'}
        </p>
      </div>

      <TradeEvaluationView trade={trade} results={results} />
    </Layout>
  )
}
